import NumberInput from '../../../components/NumberInput'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, ChevronDown, ChevronUp, Clock } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'
import { FoodSearchField } from './FoodSearchField'

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack']

// Round to 1 decimal place, drop trailing .0 visually.
function roundMacro(n) {
  if (n == null || !Number.isFinite(n)) return ''
  const r = Math.round(n * 10) / 10
  return r % 1 === 0 ? String(r) : r.toFixed(1)
}

export function MealForm({
  initialValues,
  defaultDate,
  defaultType = 'breakfast',
  recentFoods = [],
  onSubmit,
  onCancel,
  submitting,
}) {
  const { t } = useTranslation()
  const today = new Date().toLocaleDateString('en-CA')
  const isEdit = !!initialValues

  const [mealType, setMealType] = useState(initialValues?.meal_type || defaultType)
  const [date, setDate] = useState(initialValues?.date || defaultDate || today)
  const [name, setName] = useState(initialValues?.name || '')
  const [kcal, setKcal] = useState(initialValues?.kcal != null ? String(initialValues.kcal) : '')
  const [protein, setProtein] = useState(initialValues?.protein_g != null ? String(initialValues.protein_g) : '')
  const [carbs, setCarbs] = useState(initialValues?.carbs_g != null ? String(initialValues.carbs_g) : '')
  const [fat, setFat] = useState(initialValues?.fat_g != null ? String(initialValues.fat_g) : '')
  const [notes, setNotes] = useState(initialValues?.notes || '')
  const [macrosOpen, setMacrosOpen] = useState(
    !!(initialValues?.protein_g != null || initialValues?.carbs_g != null || initialValues?.fat_g != null)
  )
  const [error, setError] = useState(null)

  // Portion scaling. baseRef stores the per-100g values from the picked
  // food (or per-instance values from a recent-foods chip — treated the
  // same way). When baseRef is set, the Portion (g) input appears and
  // changing it re-scales kcal+macros. Manual edits to kcal/macros
  // remain authoritative until the user changes the portion again.
  const baseRef = useRef(null)
  const [portionG, setPortionG] = useState('')

  const applyPortion = (g) => {
    const base = baseRef.current
    if (!base) return
    const factor = Number(g) / 100
    if (!Number.isFinite(factor) || factor < 0) return
    if (base.kcal != null) setKcal(String(Math.round(base.kcal * factor)))
    if (base.protein != null) setProtein(roundMacro(base.protein * factor))
    if (base.carbs != null) setCarbs(roundMacro(base.carbs * factor))
    if (base.fat != null) setFat(roundMacro(base.fat * factor))
  }

  // Shared hydration path used by both search results and recent-foods chips.
  // `kind` distinguishes 'per100g' (from OFF — portion scaling makes sense)
  // from 'instance' (from a recent meal — was already a portion, default 100).
  const hydrateFromFood = (food, kind = 'per100g') => {
    const compositeName = food.brand ? `${food.name} (${food.brand})` : food.name
    setName(compositeName)
    baseRef.current = {
      kcal: food.kcal,
      protein: food.protein,
      carbs: food.carbs,
      fat: food.fat,
    }
    const defaultPortion = kind === 'per100g' ? '100' : '100'
    setPortionG(defaultPortion)
    if (food.kcal != null) setKcal(String(food.kcal))
    if (food.protein != null) setProtein(roundMacro(food.protein))
    if (food.carbs != null) setCarbs(roundMacro(food.carbs))
    if (food.fat != null) setFat(roundMacro(food.fat))
    if (food.protein != null || food.carbs != null || food.fat != null) {
      setMacrosOpen(true)
    }
  }

  const handlePickFood = (food) => hydrateFromFood(food, 'per100g')

  const handlePickRecent = (recent) => {
    hydrateFromFood({
      name: recent.name,
      brand: null,
      kcal: recent.kcal,
      protein: recent.protein,
      carbs: recent.carbs,
      fat: recent.fat,
    }, 'instance')
  }

  const handlePortionChange = (g) => {
    setPortionG(g)
    applyPortion(g)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError(t('phoenixTracker.meals.form.errorName'))
      return
    }
    const kcalNum = Number(kcal)
    if (kcal === '' || !Number.isFinite(kcalNum) || kcalNum < 0) {
      setError(t('phoenixTracker.meals.form.errorKcal'))
      return
    }

    await onSubmit({
      meal_type: mealType,
      date,
      name: name.trim(),
      kcal: kcalNum,
      protein_g: protein,
      carbs_g: carbs,
      fat_g: fat,
      notes,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Recent foods chip row — create mode only, only when there's history.
          Tap a chip to hydrate the form with that meal's values. */}
      {!isEdit && recentFoods.length > 0 && (
        <div>
          <label className="flex items-center gap-1 text-xs text-muted-foreground mb-1.5">
            <Clock className="w-3 h-3" />
            {t('phoenixTracker.meals.form.recent')}
          </label>
          <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
            {recentFoods.map((rf) => (
              <button
                key={rf.name}
                type="button"
                onClick={() => handlePickRecent(rf)}
                className="h-8 px-3 rounded-full text-xs font-medium whitespace-nowrap bg-muted text-muted-foreground hover:bg-accent hover:text-foreground transition-colors flex-shrink-0"
              >
                {rf.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search Open Food Facts to pre-fill the form. Skipped in edit mode
          to avoid an accidental overwrite of the row the user is editing. */}
      {!isEdit && <FoodSearchField onPick={handlePickFood} />}

      {/* Meal type chips */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1.5">
          {t('phoenixTracker.meals.form.type')}
        </label>
        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          {MEAL_TYPES.map((mt) => (
            <button
              key={mt}
              type="button"
              onClick={() => setMealType(mt)}
              className={cn(
                'h-9 px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0',
                mealType === mt
                  ? 'bg-app-primary-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {t(`phoenixTracker.meals.types.${mt}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Date */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('phoenixTracker.weight.date')}
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
          dir="ltr"
          className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm font-mono"
        />
      </div>

      {/* Name */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('phoenixTracker.meals.form.name')}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('phoenixTracker.meals.form.namePlaceholder')}
          required
          maxLength={120}
          className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm"
        />
      </div>

      {/* Portion (g) — only when a food was picked. Scales kcal+macros. */}
      {baseRef.current && (
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            {t('phoenixTracker.meals.form.portion')}
          </label>
          <NumberInput
            inputMode="numeric"
            step="10"
            min="0"
            max="9999"
            value={portionG}
            onChange={(e) => handlePortionChange(e.target.value)}
            placeholder="100"
            dir="ltr"
            className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm font-mono"
          />
        </div>
      )}

      {/* Kcal */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('phoenixTracker.meals.form.kcal')}
        </label>
        <NumberInput
          inputMode="numeric"
          step="1"
          min="0"
          max="9999"
          value={kcal}
          onChange={(e) => setKcal(e.target.value)}
          placeholder="0"
          dir="ltr"
          required
          className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm font-mono"
        />
      </div>

      {/* Macros (collapsible) */}
      <div className="rounded-lg border border-input">
        <button
          type="button"
          onClick={() => setMacrosOpen((o) => !o)}
          className="flex items-center justify-between w-full px-3 py-2.5"
          aria-expanded={macrosOpen}
        >
          <span className="text-xs text-muted-foreground">
            {t('phoenixTracker.meals.form.macrosToggle')}
          </span>
          {macrosOpen
            ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
            : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
        </button>
        {macrosOpen && (
          <div className="px-3 pb-3 grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] text-muted-foreground mb-1">
                {t('phoenixTracker.meals.form.protein')}
              </label>
              <NumberInput
                inputMode="decimal"
                step="0.1"
                min="0"
                max="999"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
                placeholder="0"
                dir="ltr"
                className="h-10 w-full px-2 rounded-md border border-input bg-background text-foreground text-sm font-mono text-center"
              />
            </div>
            <div>
              <label className="block text-[10px] text-muted-foreground mb-1">
                {t('phoenixTracker.meals.form.carbs')}
              </label>
              <NumberInput
                inputMode="decimal"
                step="0.1"
                min="0"
                max="999"
                value={carbs}
                onChange={(e) => setCarbs(e.target.value)}
                placeholder="0"
                dir="ltr"
                className="h-10 w-full px-2 rounded-md border border-input bg-background text-foreground text-sm font-mono text-center"
              />
            </div>
            <div>
              <label className="block text-[10px] text-muted-foreground mb-1">
                {t('phoenixTracker.meals.form.fat')}
              </label>
              <NumberInput
                inputMode="decimal"
                step="0.1"
                min="0"
                max="999"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
                placeholder="0"
                dir="ltr"
                className="h-10 w-full px-2 rounded-md border border-input bg-background text-foreground text-sm font-mono text-center"
              />
            </div>
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('phoenixTracker.meals.form.notes')}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('phoenixTracker.meals.form.notesPlaceholder')}
          rows={2}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm resize-none"
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            {t('phoenixTracker.meals.form.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-app-primary-500 hover:bg-app-primary-600 text-white"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin me-2" /> {t('phoenixTracker.meals.form.saving')}</>
          ) : (
            t('phoenixTracker.meals.form.save')
          )}
        </Button>
      </div>
    </form>
  )
}
